import {
    IonCard,
    IonCardContent,
    IonIcon,
    IonRow,
    IonText,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { Form, Formik, FormikHelpers } from 'formik';
import { hammerOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import * as Yup from 'yup';

import RoomHeaters from '../../components/RoomHeaters';
import LoadableContent from '../../components/content/LoadableContent';
import LoadingButton from '../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../components/page/InterventionPage';
import { IRoomWithDevicesAndFwInfo } from '../../interfaces/IOdooRoomsResponse';
import {
    accessTokenAtom,
    deepomaticAtom,
    jobIdAtom,
    roomsWithDevicesAndInterventionStateAtom,
    skipTestsAtom,
    tabsEnabledAtom,
    testsPassedAtom,
    updateDBStateAtom,
} from '../../store/Atoms';
import { updateDeviceRequest } from '../../store/Requests';
import { THomeAssetDataWithFWAndCalibration } from '../../types/HomeAssetData';
import { ODOO_INST_TYPE } from '../../utils/constants';
import { checkAssignDeepomaticTaskgroupIdx } from '../../utils/deepomatic';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../utils/hooks/usePhotosUploaded';

type DeviceInfo = {
    id: number;
    screen: boolean;
    filPilote: boolean;
    setting: string;
    thermostat: number;
    macAddress: string;
    dboxHeaterRelativeLocation: string;
    dboxAirFlowQuality: string;
    dboxCloseToHeater: string;
};

type DevicesFormValues = {
    devices: DeviceInfo[];
};

const DevicesSchema = Yup.object().shape({
    devices: Yup.array().of(
        Yup.object().shape({
            id: Yup.number(),
            dboxHeaterRelativeLocation: Yup.string().required(
                t`Please select an option`
            ),
            dboxAirFlowQuality: Yup.string().required(
                t`Please select an option`
            ),
            dboxCloseToHeater: Yup.string().required(
                t`Please select an option`
            ),
            screen: Yup.boolean(),
            filPilote: Yup.boolean(),
            setting: Yup.string().when(['filPilote', 'screen'], {
                is: (filPilote: boolean, screen: boolean) =>
                    filPilote && screen,
                then: (schema) => schema.required(t`Please select an option`),
            }),

            thermostat: Yup.number().when(['filPilote', 'screen'], {
                is: (filPilote: boolean, screen: boolean) =>
                    filPilote && screen,
                then: (schema) => schema.required(t`Please select an option`),
            }),
        })
    ),
});

const TabTest: React.FC = memo(() => {
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [jobId] = useAtom(jobIdAtom);
    const photosUploaded = usePhotosUploaded();
    const [accessToken] = useAtom(accessTokenAtom);
    const [, setTabsEnabled] = useAtom(tabsEnabledAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [value] = useAtom(roomsWithDevicesAndInterventionStateAtom);
    const [deepomatic, setDeepomatic] = useAtom(deepomaticAtom);
    const [skipTests] = useAtom(skipTestsAtom);
    const [testsPassed] = useAtom(testsPassedAtom);
    const hasData = useMemo(() => value.state === 'hasData', [value]);
    const formRef = useRef(null);
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data.rooms : []),
        [value]
    );
    const isRepair = useMemo(
        () =>
            value.state === 'hasData'
                ? value.data.jobDetails.typeInstallation ===
                  ODOO_INST_TYPE.REPAIR
                : false,
        [value]
    );
    const deepomaticWorkOrderId = useMemo(
        () =>
            value.state === 'hasData'
                ? value.data.jobDetails.imageClassificationWorkOrderId
                : undefined,
        [value]
    );
    const devices = useMemo(
        () =>
            rooms
                ? rooms.reduce(
                      (acc: THomeAssetDataWithFWAndCalibration[], room) => {
                          const roomDevices = room.devices;
                          if (roomDevices.length > 0) {
                              roomDevices.forEach((device) => {
                                  acc.push(device);
                              });
                          }
                          return acc;
                      },
                      [] as THomeAssetDataWithFWAndCalibration[]
                  )
                : [],
        [rooms]
    );
    const assetsLength = useMemo(
        () =>
            rooms
                ? rooms.reduce((acc, room) => {
                      const roomDevices = room.devices;
                      roomDevices.forEach((device) => {
                          if (device.isNew) {
                              acc += 1;
                          }
                      });
                      return acc;
                  }, 0)
                : 0,
        [rooms]
    );
    const allTestsPassed = useMemo(
        () =>
            assetsLength ===
            Object.keys(testsPassed).filter((key) => {
                const device = devices.find((d) => d.homeAssetId === key);
                if (!device || device.isNew === false) {
                    return false;
                }
                if (
                    testsPassed[key].self === true ||
                    (testsPassed[key].min === true &&
                        testsPassed[key].max === true)
                ) {
                    return true;
                }
                return false;
            }).length,
        [assetsLength, devices, testsPassed]
    );
    const canProceed = (skipTests || allTestsPassed) && photosUploaded;
    const formInitialValues = useMemo(() => {
        return rooms
            ? rooms.reduce((acc: DeviceInfo[], room) => {
                  const roomDevices = room.devices;
                  if (roomDevices.length > 0) {
                      roomDevices.forEach((device) => {
                          acc.push({
                              id: device.id,
                              screen: device.screen,
                              filPilote: device.filPilote,
                              setting: device.setting,
                              thermostat: device.thermostat,
                              macAddress: device.macAddress,
                              dboxHeaterRelativeLocation:
                                  device.dboxHeaterRelativeLocation || '',
                              dboxAirFlowQuality:
                                  device.dboxAirFlowQuality || '',
                              dboxCloseToHeater:
                                  device.dboxCloseToHeater || '',
                          });
                      });
                  }
                  return acc;
              }, [])
            : [];
    }, [rooms]);

    const handleFormSubmit = useCallback(
        async (
            data: DevicesFormValues,
            actions: FormikHelpers<DevicesFormValues>
        ) => {
            actions.setSubmitting(true);

            try {
                const updateDevicesPromises = data.devices.map((device) => {
                    const { id, ...deviceData } = device;
                    return updateDeviceRequest(
                        accessToken,
                        jobId,
                        id,
                        deviceData
                    );
                });
                await Promise.all(updateDevicesPromises);

                setTabsEnabled((prevState) => {
                    return { ...prevState, doc: true };
                });
                await updateDBState();
                router.push('doc', 'none');
            } catch (e: any) {
                const response = e.response;
                const errorMessage = response.devices
                    ? response.devices.reduce(
                          (acc: string, message: string) => {
                              return `${acc}${message}<br/>`;
                          },
                          ''
                      )
                    : e.reason;
                presentToast({
                    message: errorMessage,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }

            actions.setSubmitting(false);
        },
        [
            accessToken,
            jobId,
            presentToast,
            router,
            setTabsEnabled,
            updateDBState,
        ]
    );

    useEffect(() => {
        if (devices) {
            devices.forEach((device) => {
                return checkAssignDeepomaticTaskgroupIdx(
                    device.macAddress,
                    deepomatic,
                    setDeepomatic,
                    updateDBState
                );
            });
        }
    }, [
        allTestsPassed,
        assetsLength,
        hasData,
        deepomatic,
        setDeepomatic,
        updateDBState,
        devices,
    ]);

    return (
        <InterventionPage title={t`Test`} withPadding>
            <LoadableContent value={value}>
                <IonCard>
                    <IonCardContent>
                        <IonText>
                            <IonIcon
                                icon={hammerOutline}
                                size="large"
                                className="ion-float-left ion-padding-end"
                            ></IonIcon>
                            <Trans>
                                Test to see if the heater follows commands and
                                then document the installation of the d-boxes
                                and heaters.
                            </Trans>
                        </IonText>
                    </IonCardContent>
                </IonCard>
                <IonCard color="info" className="ion-no-margin ion-margin-top">
                    <IonCardContent>
                        <Trans>
                            Please fill in all heater details and complete the
                            heating tests.
                        </Trans>
                    </IonCardContent>
                </IonCard>
                <Formik
                    initialValues={{ devices: formInitialValues }}
                    validationSchema={DevicesSchema}
                    validateOnMount={true}
                    onSubmit={handleFormSubmit}
                    innerRef={formRef}
                >
                    {({ isSubmitting, isValid }) => {
                        let prevCalculatedIndex: number | undefined;
                        return (
                            <Form>
                                {rooms.map(
                                    (
                                        roomWithDevices: IRoomWithDevicesAndFwInfo,
                                        index
                                    ) => {
                                        const calculatedIndex =
                                            prevCalculatedIndex === undefined
                                                ? 0
                                                : prevCalculatedIndex +
                                                  rooms[index - 1].devices
                                                      .length;
                                        prevCalculatedIndex = calculatedIndex;
                                        return (
                                            <div key={roomWithDevices.roomId}>
                                                <IonRow>
                                                    <h3>
                                                        {roomWithDevices.name}
                                                    </h3>
                                                </IonRow>
                                                <RoomHeaters
                                                    index={calculatedIndex}
                                                    roomWithDevices={
                                                        roomWithDevices
                                                    }
                                                    deepomaticWorkOrderId={
                                                        deepomaticWorkOrderId
                                                    }
                                                    formRef={formRef}
                                                />
                                            </div>
                                        );
                                    }
                                )}
                                {!(skipTests || allTestsPassed) && (
                                    <IonCard
                                        color="warning"
                                        className="ion-no-margin"
                                    >
                                        <IonCardContent>
                                            {isRepair ? (
                                                <Trans>
                                                    Please perform heater test
                                                    for newly installed or
                                                    repaired heaters before
                                                    proceeding. If you cannot,
                                                    please contact your
                                                    supervisor
                                                </Trans>
                                            ) : (
                                                <Trans>
                                                    Please make sure to
                                                    complete all the heater
                                                    tests. If you cannot,
                                                    please contact your
                                                    supervisor
                                                </Trans>
                                            )}
                                        </IonCardContent>
                                    </IonCard>
                                )}
                                <LoadingButton
                                    className="ion-margin-top"
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={!canProceed || !isValid}
                                >
                                    <Trans>Proceed</Trans>
                                </LoadingButton>
                            </Form>
                        );
                    }}
                </Formik>
            </LoadableContent>
        </InterventionPage>
    );
});

export default TabTest;
